import { useMemo, useState, useEffect } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import {
  formatCurrency,
  getCurrencySymbol,
  convertToBaseCurrency,
  convertCurrency,
  refreshExchangeRates,
  getExchangeRateCacheInfo,
  sumExpensesInTripBaseCurrency,
  type ExpenseLikeForBaseSum,
} from '@/utils/currency';
import { CircularProgress } from './CircularProgress';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { Expense } from '@/services/expenses';
import type { TripBudgetSnapshot, BudgetCategoryAllocations, BudgetCategoryKey } from '@/types/budget';
import { hapticSelection } from '@/utils/haptics';
import { getInclusiveDayCount } from '@/utils/dateKeys';
import { normalizeBudgetCategoryAllocations } from '@/utils/budgetAllocations';
import { NumberStepper } from '@/components/ui/NumberStepper';

type BudgetCategory = {
  label: string;
  key: BudgetCategoryKey;
  bookingTypes?: Array<'flight' | 'hotel' | 'car' | 'restaurant' | 'activity' | 'other'>;
};

const BUDGET_CATEGORIES: BudgetCategory[] = [
  { label: 'Flights', key: 'flight', bookingTypes: ['flight'] },
  { label: 'Lodging', key: 'lodging', bookingTypes: ['hotel'] },
  { label: 'Food & Dining', key: 'food', bookingTypes: ['restaurant'] },
  { label: 'Transportation', key: 'transport', bookingTypes: ['car'] },
  { label: 'Activities', key: 'activity', bookingTypes: ['activity'] },
  { label: 'Shopping', key: 'shopping' },
  { label: 'Other', key: 'other' },
];

const BUDGET_CATEGORY_KEY_SET = new Set<BudgetCategoryKey>(BUDGET_CATEGORIES.map((c) => c.key));

const emptyCategorySpendTotals = (): Record<BudgetCategoryKey, number> => ({
  flight: 0,
  lodging: 0,
  food: 0,
  transport: 0,
  activity: 0,
  shopping: 0,
  other: 0,
});

const resolveExpenseBudgetCategory = (expense: Expense): BudgetCategoryKey => {
  const k = expense.category as BudgetCategoryKey;
  return BUDGET_CATEGORY_KEY_SET.has(k) ? k : 'other';
};

type BudgetSummaryProps = {
  trip: Trip;
  bookings: Booking[];
  expenses: Expense[];
  budgetSnapshot?: TripBudgetSnapshot; // Optional: pre-calculated budget snapshot with conversions
  onOpenBudgetEditor?: () => void;
  canEdit?: boolean;
  onSaveCategoryAllocations?: (allocations: BudgetCategoryAllocations) => Promise<void>;
};

export function BudgetSummary({
  trip,
  bookings,
  expenses,
  budgetSnapshot,
  onOpenBudgetEditor,
  canEdit = false,
  onSaveCategoryAllocations,
}: BudgetSummaryProps) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  /** Horizontal category chips: avoid fixed 180px clipping long amounts on narrow phones. */
  const categoryChipWidth = Math.round(Math.min(214, Math.max(148, windowWidth * 0.42)));
  /** Side-by-side Spent/Remaining clips long amounts (e.g. C$5,087…) on narrow Android; stack instead. */
  const stackSpentRemaining = windowWidth < 440;
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showSpendingSources, setShowSpendingSources] = useState(false);
  const [showCategoryAllocationEditor, setShowCategoryAllocationEditor] = useState(false);
  const [categoryPercents, setCategoryPercents] = useState<Record<BudgetCategoryKey, number>>({
    flight: 35,
    lodging: 30,
    food: 15,
    transport: 8,
    activity: 7,
    shopping: 3,
    other: 2,
  });
  const [savingCategoryAllocations, setSavingCategoryAllocations] = useState(false);
  const [fallbackTotalSpent, setFallbackTotalSpent] = useState<number | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  // Per-currency converted amounts for inline display
  const [convertedAmounts, setConvertedAmounts] = useState<Record<string, number | null>>({});
  // Rate refresh state
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);
  const [lastRateUpdate, setLastRateUpdate] = useState<Date | null>(null);
  const [categorySpendInBase, setCategorySpendInBase] =
    useState<Record<BudgetCategoryKey, number>>(() => emptyCategorySpendTotals());
  const [categorySpendMixedUnresolved, setCategorySpendMixedUnresolved] = useState<
    Partial<Record<BudgetCategoryKey, boolean>>
  >({});

  const baseCurrency = trip.baseCurrency || 'USD';
  const categoryAllocations = useMemo(
    () => normalizeBudgetCategoryAllocations(trip.budgetCategoryAllocations),
    [trip.budgetCategoryAllocations],
  );

  // Keep inline editor in sync with persisted values.
  useEffect(() => {
    const keys: BudgetCategoryKey[] = ['flight', 'lodging', 'food', 'transport', 'activity', 'shopping', 'other'];
    const next: Record<BudgetCategoryKey, number> = {
      flight: 35,
      lodging: 30,
      food: 15,
      transport: 8,
      activity: 7,
      shopping: 3,
      other: 2,
    };
    let running = 0;
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (i === keys.length - 1) {
        next[k] = Math.max(0, 100 - running);
      } else {
        const pct = Math.round((categoryAllocations[k] || 0) * 100);
        next[k] = Math.max(0, Math.min(100, pct));
        running += next[k];
      }
    }
    setCategoryPercents(next);
  }, [categoryAllocations]);

  // Initialize last rate update from cache info
  useEffect(() => {
    const cacheInfo = getExchangeRateCacheInfo();
    if (cacheInfo.lastUpdated) {
      setLastRateUpdate(cacheInfo.lastUpdated);
    }
  }, []);

  // Aggregate amounts by currency for fallback conversion
  const aggregatedAmounts = useMemo(() => {
    const totals: Record<string, number> = {};
    
    bookings.forEach(booking => {
      if (booking.amount && !isNaN(booking.amount)) {
        const currency = (booking.currency || baseCurrency).toUpperCase();
        totals[currency] = (totals[currency] || 0) + booking.amount;
      }
    });

    expenses.forEach(expense => {
      if (expense.amount && !isNaN(expense.amount)) {
        const currency = (expense.currency || baseCurrency).toUpperCase();
        totals[currency] = (totals[currency] || 0) + expense.amount;
      }
    });

    return totals;
  }, [bookings, expenses, baseCurrency]);

  // Perform fallback currency conversion when snapshot not available
  useEffect(() => {
    if (budgetSnapshot) {
      setFallbackTotalSpent(null);
      setConversionError(null);
      return;
    }

    const entries = Object.entries(aggregatedAmounts);
    if (entries.length === 0) {
      setFallbackTotalSpent(0);
      return;
    }

    // If all amounts are in base currency, no conversion needed
    if (entries.length === 1 && entries[0][0] === baseCurrency.toUpperCase()) {
      setFallbackTotalSpent(entries[0][1]);
      return;
    }

    // Need to convert currencies
    setIsConverting(true);
    setConversionError(null);

    const amounts = entries.map(([currency, amount]) => ({ amount, currency }));
    
    convertToBaseCurrency(amounts, baseCurrency)
      .then((converted) => {
        setFallbackTotalSpent(converted);
        setIsConverting(false);
      })
      .catch((error) => {
        console.error('[BudgetSummary] Currency conversion failed:', error);
        // Fallback: sum only base currency amounts
        const baseTotal = aggregatedAmounts[baseCurrency.toUpperCase()] || 0;
        setFallbackTotalSpent(baseTotal);
        setConversionError('Some currencies could not be converted. Showing partial total.');
        setIsConverting(false);
      });
  }, [budgetSnapshot, aggregatedAmounts, baseCurrency]);

  // Calculate total spending - use snapshot if available, otherwise use fallback
  const totalSpent = useMemo(() => {
    if (budgetSnapshot) {
      return budgetSnapshot.expensesConverted + budgetSnapshot.bookingsConverted;
    }
    return fallbackTotalSpent ?? 0;
  }, [budgetSnapshot, fallbackTotalSpent]);

  // Get currency breakdown from snapshot or aggregated amounts
  const currencyBreakdown = useMemo(() => {
    let combined: Record<string, number> = {};
    
    if (budgetSnapshot) {
      const expenseTotals = budgetSnapshot.expenseTotalsByCurrency || {};
      const bookingTotals = budgetSnapshot.bookingTotalsByCurrency || {};
      
      // Combine totals by currency
      Object.entries(expenseTotals).forEach(([currency, amount]) => {
        combined[currency] = (combined[currency] || 0) + amount;
      });
      Object.entries(bookingTotals).forEach(([currency, amount]) => {
        combined[currency] = (combined[currency] || 0) + amount;
      });
    } else {
      // Use aggregated amounts from fallback calculation
      combined = { ...aggregatedAmounts };
    }
    
    // Return all currencies (including base) for better transparency
    return Object.entries(combined)
      .filter(([, amount]) => amount > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [budgetSnapshot, aggregatedAmounts]);

  // Check if we have multiple currencies
  const hasMultipleCurrencies = currencyBreakdown.length > 1 || 
    (currencyBreakdown.length === 1 && currencyBreakdown[0][0].toUpperCase() !== baseCurrency.toUpperCase());

  // Calculate converted amounts for each currency when breakdown is shown
  useEffect(() => {
    if (!showBreakdown || !hasMultipleCurrencies) return;

    const convertAll = async () => {
      const newConvertedAmounts: Record<string, number | null> = {};
      
      for (const [currency, amount] of currencyBreakdown) {
        const upperCurrency = currency.toUpperCase();
        // Skip base currency - no conversion needed
        if (upperCurrency === baseCurrency.toUpperCase()) {
          newConvertedAmounts[upperCurrency] = amount;
          continue;
        }
        
        try {
          const converted = await convertCurrency(amount, currency, baseCurrency);
          newConvertedAmounts[upperCurrency] = converted;
        } catch (error) {
          console.warn(`[BudgetSummary] Failed to convert ${currency}:`, error);
          newConvertedAmounts[upperCurrency] = null;
        }
      }
      
      setConvertedAmounts(newConvertedAmounts);
    };

    convertAll();
  }, [showBreakdown, hasMultipleCurrencies, currencyBreakdown, baseCurrency]);

  const budget = trip.budget || 0;

  // Calculate percentage used
  const percentageUsed = budget > 0 ? Math.min(100, Math.round((totalSpent / budget) * 100)) : 0;
  const remaining = budget > 0 ? Math.max(0, budget - totalSpent) : 0;

  // Calculate days in trip
  const daysInTrip = useMemo(() => {
    if (!trip.startDate || !trip.endDate) return 0;
    return getInclusiveDayCount(trip.startDate, trip.endDate);
  }, [trip.startDate, trip.endDate]);

  // Calculate daily spending rate
  const dailySpendingRate = useMemo(() => {
    if (daysInTrip === 0) return 0;
    const today = new Date();
    const tripStart = trip.startDate || today;
    const daysElapsed = Math.max(0, Math.ceil((today.getTime() - tripStart.getTime()) / (1000 * 60 * 60 * 24)));
    if (daysElapsed === 0) return 0;
    return totalSpent / daysElapsed;
  }, [totalSpent, trip.startDate, daysInTrip]);

  // Calculate spending sources breakdown - shows where the budget numbers come from
  const spendingSources = useMemo(() => {
    const bookingsByType: Record<string, { count: number; total: number; items: Array<{ name: string; amount: number; currency: string }> }> = {};
    let bookingsTotal = 0;
    let bookingsCount = 0;
    let bookingsWithoutAmount = 0;

    const bookingsWithoutAmountDetails: Array<{ name: string; type: string }> = [];
    
    bookings.forEach(booking => {
      if (booking.amount && !isNaN(booking.amount)) {
        const type = booking.type || 'other';
        if (!bookingsByType[type]) {
          bookingsByType[type] = { count: 0, total: 0, items: [] };
        }
        bookingsByType[type].count++;
        bookingsByType[type].total += booking.amount;
        bookingsByType[type].items.push({
          name: booking.provider || booking.location || booking.confirmationNumber || `${type} booking`,
          amount: booking.amount,
          currency: booking.currency || baseCurrency,
        });
        bookingsTotal += booking.amount;
        bookingsCount++;
      } else {
        // Track bookings without extracted amounts
        bookingsWithoutAmount++;
        bookingsWithoutAmountDetails.push({
          name: booking.provider || booking.location || booking.confirmationNumber || `${booking.type || 'Unknown'} booking`,
          type: booking.type || 'other',
        });
      }
    });

    let expensesTotal = 0;
    let expensesCount = expenses.filter(e => e.amount && !isNaN(e.amount)).length;
    const expensesBySource: Record<string, { count: number; total: number }> = {
      user_entered: { count: 0, total: 0 },
      ai_estimated: { count: 0, total: 0 },
      imported_booking: { count: 0, total: 0 },
    };
    expenses.forEach(expense => {
      if (expense.amount && !isNaN(expense.amount)) {
        expensesTotal += expense.amount;
        const source = (expense.source || 'user_entered') as string;
        if (!expensesBySource[source]) expensesBySource[source] = { count: 0, total: 0 };
        expensesBySource[source].count += 1;
        expensesBySource[source].total += expense.amount;
      }
    });

    return {
      bookingsByType,
      bookingsTotal,
      bookingsCount,
      bookingsWithoutAmount, // Track bookings that don't have amounts
      bookingsWithoutAmountDetails, // Details for UI display
      expensesTotal,
      expensesCount,
      expensesBySource,
      totalBookings: bookings.length, // Total including those without amounts
      hasData: bookingsCount > 0 || expensesCount > 0,
      hasAnyBookings: bookings.length > 0,
    };
  }, [bookings, expenses, baseCurrency]);

  // Calculate remaining days
  const remainingDays = useMemo(() => {
    if (!trip.endDate) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(trip.endDate);
    endDate.setHours(0, 0, 0, 0);
    const diff = endDate.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [trip.endDate]);

  const status = useMemo(() => {
    if (budget === 0) {
      return {
        tone: 'neutral' as const,
        label: 'No budget set',
        color: theme.colors.textSecondary,
        description: 'Add a budget to track progress',
      };
    }

    if (percentageUsed >= 100) {
      return {
        tone: 'critical' as const,
        label: 'Over budget',
        color: theme.colors.error,
        description: 'Reduce spend or raise your cap',
      };
    }

    if (percentageUsed >= 90) {
      return {
        tone: 'warning' as const,
        label: 'Nearing limit',
        color: theme.colors.warning,
        description: 'Keep daily spend tight',
      };
    }

    if (percentageUsed >= 75) {
      return {
        tone: 'caution' as const,
        label: 'Watch spend',
        color: theme.colors.warning,
        description: 'You’re trending high',
      };
    }

    return {
      tone: 'success' as const,
      label: 'On track',
      color: theme.colors.success,
      description: 'Healthy pace for this trip',
    };
  }, [budget, percentageUsed, theme.colors.error, theme.colors.success, theme.colors.textSecondary, theme.colors.warning]);

  useEffect(() => {
    let cancelled = false;

    const buildFallbackTotalsAndMixedHints = (): {
      spent: Record<BudgetCategoryKey, number>;
      mixed: Partial<Record<BudgetCategoryKey, boolean>>;
    } => {
      const spending = emptyCategorySpendTotals();
      const mixed: Partial<Record<BudgetCategoryKey, boolean>> = {};

      bookings.forEach((booking) => {
        if (!booking.amount || !Number.isFinite(booking.amount) || !booking.currency) return;

        const categoryMeta = BUDGET_CATEGORIES.find((cat) =>
          cat.bookingTypes?.includes(booking.type as any),
        );
        const categoryKey: BudgetCategoryKey = categoryMeta?.key ?? 'other';

        if (booking.currency.toUpperCase() === baseCurrency.toUpperCase()) {
          spending[categoryKey] += booking.amount;
        } else {
          mixed[categoryKey] = true;
        }
      });

      expenses.forEach((expense) => {
        if (!expense.amount || !Number.isFinite(expense.amount) || !expense.currency) return;
        const categoryKey = resolveExpenseBudgetCategory(expense);
        const base = baseCurrency.toUpperCase();
        const cur = expense.currency.toUpperCase();

        if (cur === base) {
          spending[categoryKey] += expense.amount;
        } else {
          const conv = expense.conversion;
          if (
            conv &&
            String(conv.toCurrency).toUpperCase() === base &&
            typeof conv.convertedAmount === 'number' &&
            Number.isFinite(conv.convertedAmount)
          ) {
            spending[categoryKey] += conv.convertedAmount;
          } else {
            mixed[categoryKey] = true;
          }
        }
      });

      return { spent: spending, mixed };
    };

    (async () => {
      const buckets: Record<BudgetCategoryKey, ExpenseLikeForBaseSum[]> = {
        flight: [],
        lodging: [],
        food: [],
        transport: [],
        activity: [],
        shopping: [],
        other: [],
      };

      bookings.forEach((booking) => {
        if (!booking.amount || !Number.isFinite(booking.amount) || !booking.currency) return;
        const categoryMeta = BUDGET_CATEGORIES.find((cat) =>
          cat.bookingTypes?.includes(booking.type as any),
        );
        const categoryKey: BudgetCategoryKey = categoryMeta?.key ?? 'other';
        buckets[categoryKey].push({
          amount: booking.amount,
          currency: booking.currency,
        });
      });

      expenses.forEach((expense) => {
        if (!expense.amount || !Number.isFinite(expense.amount) || !expense.currency) return;
        const categoryKey = resolveExpenseBudgetCategory(expense);
        const c = expense.conversion;
        buckets[categoryKey].push({
          amount: expense.amount,
          currency: expense.currency,
          conversion:
            c !== undefined
              ? {
                  toCurrency: c.toCurrency,
                  convertedAmount: c.convertedAmount,
                  exchangeRate: c.exchangeRate,
                }
              : undefined,
        });
      });

      try {
        const next = emptyCategorySpendTotals();
        await Promise.all(
          BUDGET_CATEGORIES.map(async ({ key }) => {
            next[key] = await sumExpensesInTripBaseCurrency(buckets[key], baseCurrency);
          }),
        );

        if (cancelled) return;
        setCategorySpendInBase(next);
        setCategorySpendMixedUnresolved({});
      } catch (err) {
        console.error('[BudgetSummary] Failed to convert spending by category:', err);
        if (cancelled) return;
        const { spent, mixed } = buildFallbackTotalsAndMixedHints();
        setCategorySpendInBase(spent);
        setCategorySpendMixedUnresolved(mixed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookings, expenses, baseCurrency]);

  const categoryStats = useMemo(() => {
    return BUDGET_CATEGORIES.map((category) => ({
      label: category.label,
      key: category.key,
      spent: categorySpendInBase[category.key],
      allocated: budget > 0 ? Math.round(budget * (categoryAllocations[category.key] || 0)) : 0,
      hasMixedCurrencies: categorySpendMixedUnresolved[category.key] === true,
    }))
      .filter((item) => item.spent > 0 || item.allocated > 0 || item.hasMixedCurrencies)
      .sort((a, b) => b.spent - a.spent || b.allocated - a.allocated)
      .slice(0, 5);
  }, [budget, categoryAllocations, categorySpendInBase, categorySpendMixedUnresolved]);

  return (
    <Card 
      variant="elevated" 
      style={{ 
        marginBottom: theme.spacing(2),
      }}
    >
      <View style={{ gap: theme.spacing(1.25) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ gap: 4, flex: 1, minWidth: 0 }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
              Trip budget
            </Caption>
            <ThemedText
              variant="h3"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{ fontSize: 21, fontWeight: '700' }}
            >
              {budget > 0 ? formatCurrency(budget, baseCurrency) : 'Not set'}
            </ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
              Total trip budget. Estimates update as you add bookings.
            </Caption>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flexShrink: 0 }}>
            <View
              style={{
                maxWidth: 140,
              paddingHorizontal: theme.spacing(1.5),
              paddingVertical: theme.spacing(0.5),
              borderRadius: theme.radii.pill,
              backgroundColor: status.color + '24',
              }}
            >
              <Body numberOfLines={1} ellipsizeMode="tail" color={status.color} style={{ fontSize: 12, fontWeight: '700' }}>
                {status.label}
              </Body>
            </View>
            {onOpenBudgetEditor && canEdit && (
              <Pressable
                onPress={() => {
                  hapticSelection();
                  onOpenBudgetEditor();
                }}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1.25),
                  paddingVertical: theme.spacing(0.75),
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <Ionicons name="create-outline" size={14} color={theme.colors.text} />
                  <Caption style={{ fontSize: 11, fontWeight: '700' }} color={theme.colors.text}>
                    Adjust
                  </Caption>
                </View>
              </Pressable>
            )}
          </View>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2), position: 'relative', zIndex: 1 }}>
          {/* Left: Circular Progress */}
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress
              size={96}
              progress={percentageUsed}
              color={status.color}
              strokeWidth={8}
            >
              <View style={{ alignItems: 'center' }}>
                <ThemedText variant="caption" color={status.color} style={{ fontSize: 12, fontWeight: '700' }}>
                  {percentageUsed}%
                </ThemedText>
                <ThemedText variant="caption" color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  used
                </ThemedText>
              </View>
            </CircularProgress>
          </View>

          {/* Right: Key Metrics */}
          <View style={{ flex: 1, gap: theme.spacing(1.25), minWidth: 0 }}>
            {/* Spent vs Remaining — equal-width columns on tablet; stacked on phones so amounts never clip */}
            {stackSpentRemaining ? (
              <View style={{ gap: theme.spacing(1.25) }}>
                <View>
                  <Caption color={theme.colors.textSecondary}>Spent</Caption>
                  <ThemedText
                    variant="h3"
                    style={{ fontSize: 20, fontWeight: '700', color: status.color, flexShrink: 0 }}
                  >
                    {formatCurrency(totalSpent, baseCurrency)}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                    Total (bookings + expenses)
                  </Caption>
                </View>
                <View>
                  <Caption color={theme.colors.textSecondary}>Remaining</Caption>
                  <ThemedText
                    variant="h3"
                    style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: remaining > 0 ? theme.colors.success : theme.colors.error,
                      flexShrink: 0,
                    }}
                  >
                    {budget > 0 ? formatCurrency(remaining, baseCurrency) : '--'}
                  </ThemedText>
                </View>
              </View>
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: theme.spacing(1.5),
                }}
              >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Caption color={theme.colors.textSecondary}>Spent</Caption>
                  <ThemedText
                    variant="h3"
                    style={{ fontSize: 20, fontWeight: '700', color: status.color }}
                    numberOfLines={1}
                  >
                    {formatCurrency(totalSpent, baseCurrency)}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                    Total (bookings + expenses)
                  </Caption>
                </View>
                <View style={{ flex: 1, minWidth: 0, alignItems: 'flex-end' }}>
                  <Caption color={theme.colors.textSecondary}>Remaining</Caption>
                  <ThemedText
                    variant="h3"
                    style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: remaining > 0 ? theme.colors.success : theme.colors.error,
                    }}
                    numberOfLines={1}
                  >
                    {budget > 0 ? formatCurrency(remaining, baseCurrency) : '--'}
                  </ThemedText>
                </View>
              </View>
            )}

            {/* Linear progress */}
            <View style={{ gap: 4 }}>
              <View style={{ height: 10, borderRadius: 999, backgroundColor: theme.colors.borderMuted + '50', overflow: 'hidden' }}>
                <View
                  style={{
                    width: `${Math.min(100, percentageUsed)}%`,
                    height: '100%',
                    borderRadius: 999,
                    backgroundColor: status.color,
                  }}
                />
              </View>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                {status.description}
              </Caption>
            </View>

            {/* Meta stats */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: theme.spacing(1) }}>
              {dailySpendingRate > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    {formatCurrency(dailySpendingRate, baseCurrency)}/day
                  </Caption>
                </View>
              )}
              {remainingDays > 0 && budget > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    {remainingDays} {remainingDays === 1 ? 'day' : 'days'} left
                  </Caption>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Currency Conversion Status */}
        {isConverting && (
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            paddingTop: theme.spacing(1),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
          }}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
              Converting currencies...
            </Caption>
          </View>
        )}

        {/* Conversion Error Warning */}
        {conversionError && (
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            paddingTop: theme.spacing(1),
            paddingHorizontal: theme.spacing(1),
            paddingVertical: theme.spacing(0.75),
            backgroundColor: theme.colors.warning + '15',
            borderRadius: theme.radii.sm,
            marginTop: theme.spacing(1),
          }}>
            <Ionicons name="warning-outline" size={14} color={theme.colors.warning} />
            <Caption color={theme.colors.warning} style={{ fontSize: 11, flex: 1 }}>
              {conversionError}
            </Caption>
          </View>
        )}

        {/* Spending Sources Toggle - Always show when there's data OR bookings without amounts */}
        {(spendingSources.hasData || spendingSources.hasAnyBookings) && (
          <Pressable
            onPress={() => {
              hapticSelection();
              setShowSpendingSources(!showSpendingSources);
            }}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: theme.spacing(1.5),
              marginTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
              <Ionicons name="receipt-outline" size={16} color={theme.colors.textSecondary} />
              <View style={{ flex: 1 }}>
                <Caption color={theme.colors.textSecondary}>
                  Spending sources ({spendingSources.bookingsCount} bookings, {spendingSources.expensesCount} expenses)
                </Caption>
                {spendingSources.bookingsWithoutAmount > 0 && (
                  <Caption color={theme.colors.warning} style={{ fontSize: 10 }}>
                    {spendingSources.bookingsWithoutAmount} {spendingSources.bookingsWithoutAmount === 1 ? 'booking' : 'bookings'} without price data
                  </Caption>
                )}
              </View>
            </View>
            <Ionicons 
              name={showSpendingSources ? "chevron-up" : "chevron-down"} 
              size={16} 
              color={theme.colors.textSecondary} 
            />
          </Pressable>
        )}

        {/* Spending Sources Details */}
        {showSpendingSources && (spendingSources.hasData || spendingSources.hasAnyBookings) && (
          <View style={{
            marginTop: theme.spacing(1.5),
            paddingTop: theme.spacing(1),
            gap: theme.spacing(1),
          }}>
            {/* Bookings by Type */}
            {Object.entries(spendingSources.bookingsByType).map(([type, data]) => {
              const typeLabels: Record<string, string> = {
                flight: '✈️ Flights',
                hotel: '🏨 Hotels',
                car: '🚗 Car Rentals',
                restaurant: '🍽️ Restaurants',
                activity: '🎫 Activities',
                other: '📋 Other Bookings',
              };
              const label = typeLabels[type] || `📋 ${type.charAt(0).toUpperCase() + type.slice(1)}`;
              
              return (
                <View 
                  key={type}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: theme.spacing(0.75),
                    paddingHorizontal: theme.spacing(1),
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.radii.sm,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <ThemedText variant="body" style={{ fontSize: 13 }}>
                      {label}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginTop: 2 }}>
                      {data.count} {data.count === 1 ? 'booking' : 'bookings'}
                    </Caption>
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                      Imported from booking
                    </Caption>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    {data.items.slice(0, 3).map((item, idx) => (
                      <Caption 
                        key={idx} 
                        color={theme.colors.textSecondary} 
                        style={{ fontSize: 10 }}
                        numberOfLines={1}
                      >
                        {item.name.substring(0, 20)}: {formatCurrency(item.amount, item.currency)}
                      </Caption>
                    ))}
                    {data.items.length > 3 && (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, fontStyle: 'italic' }}>
                        +{data.items.length - 3} more
                      </Caption>
                    )}
                  </View>
                </View>
              );
            })}

            {/* Expenses by Source */}
            {spendingSources.expensesCount > 0 && (
              <View style={{ gap: theme.spacing(1) }}>
                {Object.entries(spendingSources.expensesBySource)
                  .filter(([, v]) => v.total > 0)
                  .map(([source, v]) => {
                    const label =
                      source === 'ai_estimated'
                        ? 'Estimated by AI'
                        : source === 'imported_booking'
                          ? 'Imported from booking'
                          : 'Entered by you';
                    return (
                      <View
                        key={source}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: theme.spacing(0.75),
                          paddingHorizontal: theme.spacing(1),
                          backgroundColor: theme.colors.surface,
                          borderRadius: theme.radii.sm,
                          borderWidth: 1,
                          borderColor: theme.colors.borderMuted,
                        }}
                      >
                        <View>
                          <ThemedText variant="body" style={{ fontSize: 13 }}>
                            {label}
                          </ThemedText>
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginTop: 2 }}>
                            {v.count} {v.count === 1 ? 'expense' : 'expenses'}
                          </Caption>
                        </View>
                        <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                          {formatCurrency(v.total, baseCurrency)}
                        </ThemedText>
                      </View>
                    );
                  })}
              </View>
            )}

            {/* Bookings Without Amounts Warning */}
            {spendingSources.bookingsWithoutAmount > 0 && (
              <View 
                style={{
                  paddingVertical: theme.spacing(0.75),
                  paddingHorizontal: theme.spacing(1),
                  backgroundColor: theme.colors.warning + '10',
                  borderRadius: theme.radii.sm,
                  borderWidth: 1,
                  borderColor: theme.colors.warning + '30',
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.5) }}>
                  <Ionicons name="warning-outline" size={14} color={theme.colors.warning} />
                  <ThemedText variant="body" style={{ fontSize: 12, color: theme.colors.warning }}>
                    {spendingSources.bookingsWithoutAmount} {spendingSources.bookingsWithoutAmount === 1 ? 'booking' : 'bookings'} without price data
                  </ThemedText>
                </View>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  These bookings were synced but price couldn't be extracted from the email:
                </Caption>
                {spendingSources.bookingsWithoutAmountDetails.slice(0, 5).map((item, idx) => (
                  <Caption key={idx} color={theme.colors.textSecondary} style={{ fontSize: 10, marginLeft: theme.spacing(1) }}>
                    • {item.name} ({item.type})
                  </Caption>
                ))}
                {spendingSources.bookingsWithoutAmountDetails.length > 5 && (
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginLeft: theme.spacing(1), fontStyle: 'italic' }}>
                    +{spendingSources.bookingsWithoutAmountDetails.length - 5} more
                  </Caption>
                )}
                <Caption color={theme.colors.primary} style={{ fontSize: 10, marginTop: theme.spacing(0.5) }}>
                  Tip: Add amounts manually via Bookings tab to include them in budget
                </Caption>
              </View>
            )}

            {/* Total Summary */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
            }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
                Total tracked
              </Caption>
              <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                {formatCurrency(totalSpent, baseCurrency)}
              </ThemedText>
            </View>
          </View>
        )}

        {/* Currency Breakdown Toggle */}
        {hasMultipleCurrencies && currencyBreakdown.length > 0 && (
          <Pressable
            onPress={() => setShowBreakdown(!showBreakdown)}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="cash-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>
                Multi-currency breakdown ({currencyBreakdown.length} {currencyBreakdown.length === 1 ? 'currency' : 'currencies'})
              </Caption>
            </View>
            <Ionicons 
              name={showBreakdown ? "chevron-up" : "chevron-down"} 
              size={16} 
              color={theme.colors.textSecondary} 
            />
          </Pressable>
        )}
      </View>

      {/* Currency Breakdown Details */}
      {showBreakdown && hasMultipleCurrencies && currencyBreakdown.length > 0 && (
        <View style={{
          marginTop: theme.spacing(1.5),
          paddingTop: theme.spacing(1.5),
          borderTopWidth: 1,
          borderTopColor: theme.colors.borderMuted,
          gap: theme.spacing(1),
        }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
              Spending by Currency
            </Caption>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              {lastRateUpdate && (
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 9 }}>
                  Rates: {lastRateUpdate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </Caption>
              )}
              <Pressable
                onPress={async () => {
                  if (isRefreshingRates) return;
                  hapticSelection();
                  setIsRefreshingRates(true);
                  
                  // Get all currencies in the breakdown (except base)
                  const currencies = currencyBreakdown
                    .map(([c]) => c.toUpperCase())
                    .filter(c => c !== baseCurrency.toUpperCase());
                  
                  try {
                    const result = await refreshExchangeRates(currencies, baseCurrency);
                    setLastRateUpdate(result.updatedAt);
                    
                    // Recalculate converted amounts with new rates
                    const newConvertedAmounts: Record<string, number | null> = {};
                    for (const [currency, amount] of currencyBreakdown) {
                      const upperCurrency = currency.toUpperCase();
                      if (upperCurrency === baseCurrency.toUpperCase()) {
                        newConvertedAmounts[upperCurrency] = amount;
                      } else {
                        try {
                          const converted = await convertCurrency(amount, currency, baseCurrency);
                          newConvertedAmounts[upperCurrency] = converted;
                        } catch {
                          newConvertedAmounts[upperCurrency] = null;
                        }
                      }
                    }
                    setConvertedAmounts(newConvertedAmounts);
                  } catch (error) {
                    console.warn('[BudgetSummary] Failed to refresh rates:', error);
                  } finally {
                    setIsRefreshingRates(false);
                  }
                }}
                style={({ pressed }) => ({
                  opacity: pressed ? 0.7 : 1,
                  padding: theme.spacing(0.5),
                })}
                disabled={isRefreshingRates}
              >
                {isRefreshingRates ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Ionicons name="refresh" size={14} color={theme.colors.primary} />
                )}
              </Pressable>
            </View>
          </View>
          {currencyBreakdown.map(([currency, amount]) => {
            const upperCurrency = currency.toUpperCase();
            const isBaseCurrency = upperCurrency === baseCurrency.toUpperCase();
            const convertedAmount = convertedAmounts[upperCurrency];
            const hasConvertedAmount = convertedAmount !== null && convertedAmount !== undefined;
            
            return (
              <View
                key={currency}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: theme.spacing(0.75),
                  paddingHorizontal: theme.spacing(1),
                  backgroundColor: isBaseCurrency ? theme.colors.primary + '10' : theme.colors.surface,
                  borderRadius: theme.radii.sm,
                  borderWidth: 1,
                  borderColor: isBaseCurrency ? theme.colors.primary + '30' : theme.colors.borderMuted,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <View style={{
                    width: 32,
                    height: 32,
                    borderRadius: theme.radii.sm,
                    backgroundColor: isBaseCurrency ? theme.colors.primary + '20' : theme.colors.borderMuted + '50',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Caption 
                      color={isBaseCurrency ? theme.colors.primary : theme.colors.textSecondary} 
                      style={{ fontSize: 11, fontWeight: '700' }}
                    >
                      {upperCurrency}
                    </Caption>
                  </View>
                  <View>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                      {formatCurrency(amount, currency)}
                    </ThemedText>
                    {isBaseCurrency && (
                      <Caption color={theme.colors.primary} style={{ fontSize: 10 }}>
                        Base currency
                      </Caption>
                    )}
                  </View>
                </View>
                {!isBaseCurrency && (
                  <View style={{ alignItems: 'flex-end' }}>
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                      Converts to
                    </Caption>
                    {hasConvertedAmount ? (
                      <ThemedText variant="bodyStrong" style={{ fontSize: 13, color: theme.colors.success }}>
                        ≈ {formatCurrency(convertedAmount, baseCurrency)}
                      </ThemedText>
                    ) : (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                        <ActivityIndicator size="small" color={theme.colors.textSecondary} />
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                          {baseCurrency}
                        </Caption>
                      </View>
                    )}
                  </View>
                )}
              </View>
            );
          })}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: theme.spacing(1),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
          }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
              Total (converted)
            </Caption>
            <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
              {formatCurrency(totalSpent, baseCurrency)}
            </ThemedText>
          </View>
        </View>
      )}

      {/* Category Quick Stats */}
      {categoryStats.length > 0 && (
        <View style={{ 
          marginTop: theme.spacing(2),
          paddingTop: theme.spacing(1.5),
          borderTopWidth: 1,
          borderTopColor: theme.colors.borderMuted,
          gap: theme.spacing(1),
        }}>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            Top categories
          </Caption>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={{ marginHorizontal: -theme.spacing(2) }}
            contentContainerStyle={{ paddingHorizontal: theme.spacing(2), gap: theme.spacing(1) }}
          >
            {categoryStats.map((stat) => {
              const percent = stat.allocated > 0 ? Math.min(100, Math.round((stat.spent / stat.allocated) * 100)) : Math.min(100, budget > 0 ? Math.round((stat.spent / budget) * 100) : 0);
              const getCategoryColor = () => {
                const colors: Record<string, string> = {
                  flight: '#3B82F6',
                  lodging: '#8B5CF6',
                  food: '#F59E0B',
                  transport: '#10B981',
                  activity: '#EC4899',
                  shopping: '#EF4444',
                  other: theme.colors.textSecondary,
                };
                return colors[stat.key] || theme.colors.primary;
              };
              const categoryColor = getCategoryColor();
              
              return (
                <View
                  key={stat.key}
                  style={{
                    width: categoryChipWidth,
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    gap: theme.spacing(0.5),
                  }}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <ThemedText
                      variant="caption"
                      style={{ fontSize: 11, fontWeight: '700', color: categoryColor, flex: 1, minWidth: 0 }}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {stat.label}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, flexShrink: 0 }} numberOfLines={1}>
                      {percent}%
                    </Caption>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(0.75) }}>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 15, flex: 1, minWidth: 0 }} numberOfLines={1} ellipsizeMode="tail">
                      {formatCurrency(stat.spent, baseCurrency)}
                    </ThemedText>
                    <Caption
                      color={theme.colors.textSecondary}
                      style={{ fontSize: 11, flexShrink: 0, maxWidth: '52%', textAlign: 'right' }}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    >
                      {stat.allocated > 0 ? formatCurrency(stat.allocated, baseCurrency) : 'No cap'}
                    </Caption>
                  </View>
                  <View style={{ height: 8, borderRadius: 999, backgroundColor: theme.colors.borderMuted + '50', overflow: 'hidden' }}>
                    <View
                      style={{
                        width: `${percent}%`,
                        height: '100%',
                        borderRadius: 999,
                        backgroundColor: categoryColor,
                      }}
                    />
                  </View>
                  {stat.hasMixedCurrencies && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                      <Ionicons name="information-circle-outline" size={12} color={theme.colors.warning} />
                      <Caption color={theme.colors.warning} style={{ fontSize: 9 }}>
                        + other currencies
                      </Caption>
                    </View>
                  )}
                  {stat.allocated > 0 && !stat.hasMixedCurrencies && (
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                      {formatCurrency(Math.max(0, stat.allocated - stat.spent), baseCurrency)} remaining
                    </Caption>
                  )}
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}

      {/* Category Allocation Editor (inline, inside summary card) */}
      {canEdit && !!onSaveCategoryAllocations && (
        <View
          style={{
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(1.5),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
            gap: theme.spacing(1),
          }}
        >
          <Pressable
            onPress={() => {
              hapticSelection();
              setShowCategoryAllocationEditor((v) => !v);
            }}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="pie-chart-outline" size={16} color={theme.colors.textSecondary} />
              <View>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                  Category allocation %
                </Caption>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  Adjust how your total budget is split across categories
                </Caption>
              </View>
            </View>
            <Ionicons
              name={showCategoryAllocationEditor ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={theme.colors.textSecondary}
            />
          </Pressable>

          {showCategoryAllocationEditor && (
            <View style={{ gap: theme.spacing(1.25) }}>
              {(() => {
                const totalPct =
                  Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0;
                if (totalPct === 100) return null;
                const isOver = totalPct > 100;
                return (
                  <View
                    style={{
                      padding: theme.spacing(1.25),
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: isOver ? theme.colors.error + '55' : theme.colors.warning + '55',
                      backgroundColor: isOver ? theme.colors.error + '10' : theme.colors.warning + '10',
                    }}
                  >
                    <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                      {isOver ? 'Total exceeds 100%' : 'Total is below 100%'}
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                      Current total: {Math.round(totalPct)}%.{' '}
                      {isOver
                        ? 'Reduce one or more categories — increases are capped until total is ≤ 100%.'
                        : 'You can allocate the remaining % across categories (or we’ll normalize to 100% on save).'}
                    </Caption>
                  </View>
                );
              })()}

              {(
                [
                  { key: 'flight', label: 'Flights' },
                  { key: 'lodging', label: 'Lodging' },
                  { key: 'food', label: 'Food & Dining' },
                  { key: 'transport', label: 'Transport' },
                  { key: 'activity', label: 'Activities' },
                  { key: 'shopping', label: 'Shopping' },
                  { key: 'other', label: 'Other' },
                ] as Array<{ key: BudgetCategoryKey; label: string }>
              ).map(({ key, label }) => {
                const value = categoryPercents[key] ?? 0;
                const total =
                  Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0;
                // Prevent going above 100%: cap this category by remaining room.
                // Example: total=103, value=30 => maxForThis=27 (only decreases allowed).
                const maxForThis = Math.max(0, Math.min(100, 100 - (total - value)));
                return (
                  <View
                    key={key}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: theme.spacing(1),
                      paddingVertical: theme.spacing(0.25),
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                        {label}
                      </ThemedText>
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        {value}% of total
                      </Caption>
                    </View>
                    <NumberStepper
                      value={value}
                      onValueChange={(next) => {
                        const clamped = Math.max(0, Math.min(maxForThis, next));
                        setCategoryPercents((prev) => ({
                          ...prev,
                          [key]: clamped,
                        }));
                      }}
                      min={0}
                      max={maxForThis}
                      step={1}
                      compact
                    />
                  </View>
                );
              })}

              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                  Total:{' '}
                  {(
                    Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0
                  ).toFixed(0)}
                  %
                </Caption>
                <Pressable
                  onPress={() => {
                    const normalized = normalizeBudgetCategoryAllocations(null);
                    setCategoryPercents({
                      flight: Math.round(normalized.flight * 100),
                      lodging: Math.round(normalized.lodging * 100),
                      food: Math.round(normalized.food * 100),
                      transport: Math.round(normalized.transport * 100),
                      activity: Math.round(normalized.activity * 100),
                      shopping: Math.round(normalized.shopping * 100),
                      other:
                        100 -
                        (Math.round(normalized.flight * 100) +
                          Math.round(normalized.lodging * 100) +
                          Math.round(normalized.food * 100) +
                          Math.round(normalized.transport * 100) +
                          Math.round(normalized.activity * 100) +
                          Math.round(normalized.shopping * 100)),
                    });
                  }}
                  style={({ pressed }) => ({
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.5),
                    borderRadius: theme.radii.pill,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Caption style={{ fontSize: 11, fontWeight: '700' }} color={theme.colors.text}>
                    Reset
                  </Caption>
                </Pressable>
              </View>

              <Pressable
                onPress={async () => {
                  if (savingCategoryAllocations) return;
                  setSavingCategoryAllocations(true);
                  try {
                    const allocationsToSave = normalizeBudgetCategoryAllocations(categoryPercents);
                    await onSaveCategoryAllocations(allocationsToSave);
                    setShowCategoryAllocationEditor(false);
                  } finally {
                    setSavingCategoryAllocations(false);
                  }
                }}
                style={({ pressed }) => ({
                  marginTop: theme.spacing(0.25),
                  paddingVertical: theme.spacing(1),
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.primary,
                  opacity: savingCategoryAllocations ? 0.6 : pressed ? 0.85 : 1,
                  alignItems: 'center',
                })}
                disabled={savingCategoryAllocations}
              >
                <Caption style={{ color: theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF', fontWeight: '700' }}>
                  {savingCategoryAllocations ? 'Saving…' : 'Save category %'}
                </Caption>
              </Pressable>
            </View>
          )}
        </View>
      )}
    </Card>
  );
}

